import React, { useCallback, useEffect, useRef, useState } from "react";
import { getLiveReadCredential } from "@/services/liveMedia";
import i18n from "@/i18n/config";
import { extractWhepReason, waitForIceGatheringComplete } from "@/utils/whepIce";

const LOW_LATENCY_JITTER_BUFFER_MS = 50;

/** Grace period before an ICE `disconnected` is treated as a hard failure. */
const DISCONNECT_GRACE_MS = 2500;
/** Reconnect backoff (ms), indexed by the running failure count. */
const BACKOFF_MS = [1000, 2000, 4000, 8000];
/** Surface a terminal `error` after this many consecutive failed attempts. */
const MAX_FAILURES = 6;
/** Abort an attempt when ICE gathering stalls this long (#379). */
const ICE_GATHERING_TIMEOUT_MS = 3000;

export type WhepStatus = "connecting" | "playing" | "reconnecting" | "error";

export interface UseWhepPlayerResult {
  videoRef: React.RefObject<HTMLVideoElement>;
  status: WhepStatus;
  error: string | null;
  needsTapToPlay: boolean;
  /** Call inside a user gesture; unmutes + video.play(); clears needsTapToPlay. */
  play: () => void;
  /** Manual retry after `error`; resets backoff. */
  retry: () => void;
}

function setReceiverBufferTarget(receiver: RTCRtpReceiver) {
  const receiverWithBuffer = receiver as RTCRtpReceiver & {
    jitterBufferTarget?: number;
  };

  if ("jitterBufferTarget" in receiverWithBuffer) {
    receiverWithBuffer.jitterBufferTarget = LOW_LATENCY_JITTER_BUFFER_MS;
  }
}

export function useWhepPlayer(src: string | undefined): UseWhepPlayerResult {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [status, setStatus] = useState<WhepStatus>(src ? "connecting" : "error");
  const [error, setError] = useState<string | null>(
    src ? null : i18n.t("pages:sessions.videoPlayback.whepUnavailable"),
  );
  const [needsTapToPlay, setNeedsTapToPlay] = useState(false);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const resourceUrlRef = useRef<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const failuresRef = useRef(0);
  /** Server-stated reason for a deterministic (non-retryable) WHEP failure. */
  const fatalReasonRef = useRef<string | null>(null);
  /** Kind of the most recent failed attempt; selects the terminal message. */
  const failureKindRef = useRef<"gathering-timeout" | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const graceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const disposedRef = useRef(false);
  const reconnectRef = useRef<() => void>(() => {});

  // Mirror `status` into a ref so the persistent event listeners can read the
  // latest value without re-subscribing on every status change.
  const statusRef = useRef(status);
  statusRef.current = status;

  const attemptPlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    void v.play().then(
      () => setNeedsTapToPlay(false),
      (e: unknown) => {
        // Only a genuine autoplay-policy block needs the tap overlay.
        // Transient AbortErrors (srcObject replaced while play() was pending)
        // clear themselves when the next ontrack re-attempts playback.
        if (e instanceof DOMException && e.name === "NotAllowedError") {
          setNeedsTapToPlay(true);
        }
      },
    );
  }, []);

  const play = useCallback(() => {
    const v = videoRef.current;
    if (v) v.muted = false;
    attemptPlay();
  }, [attemptPlay]);

  useEffect(() => {
    disposedRef.current = false;

    if (!src) {
      setStatus("error");
      setError(i18n.t("pages:sessions.videoPlayback.whepUnavailable"));
      return () => {
        disposedRef.current = true;
      };
    }

    if (!window.RTCPeerConnection) {
      setStatus("error");
      setError(i18n.t("pages:sessions.videoPlayback.webrtcUnsupported"));
      return () => {
        disposedRef.current = true;
      };
    }

    // Capture the narrowed (non-undefined) src; the hoisted `connect`
    // declaration otherwise loses the `if (!src)` narrowing.
    const whepUrl = src;
    failuresRef.current = 0;
    fatalReasonRef.current = null;
    failureKindRef.current = null;

    function clearReconnectTimers() {
      if (reconnectTimerRef.current !== null) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (graceTimerRef.current !== null) {
        clearTimeout(graceTimerRef.current);
        graceTimerRef.current = null;
      }
    }

    function scheduleReconnect(extraDelay = 0) {
      if (disposedRef.current) return;
      // Entering a non-playing state invalidates any pending tap-to-play:
      // there is no live media to tap for until tracks arrive again.
      setNeedsTapToPlay(false);
      setStatus("reconnecting");
      const n = Math.min(failuresRef.current, BACKOFF_MS.length - 1);
      if (failuresRef.current >= MAX_FAILURES) {
        setStatus("error");
        const reason = fatalReasonRef.current;
        fatalReasonRef.current = null;
        const kind = failureKindRef.current;
        failureKindRef.current = null;
        setError(
          reason
            ? i18n.t("pages:sessions.videoPlayback.reconnectFailedReason", {
                reason,
              })
            : kind === "gathering-timeout"
              ? i18n.t("pages:sessions.videoPlayback.connectionTimedOut")
              : i18n.t("pages:sessions.videoPlayback.reconnectFailed"),
        );
        return;
      }
      if (reconnectTimerRef.current !== null) {
        clearTimeout(reconnectTimerRef.current);
      }
      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null;
        failuresRef.current += 1;
        startConnect();
      }, BACKOFF_MS[n] + extraDelay);
    }

    async function connect() {
      if (disposedRef.current) return;

      // Each attempt owns its outcome: a stale failure kind from a prior
      // attempt must not select the terminal message if this attempt fails
      // through a path that never re-sets it (e.g. pc "failed"/"disconnected").
      failureKindRef.current = null;

      // A fresh negotiation cycle has no media yet; any tap-to-play state
      // from a previous cycle is stale and would mask the connecting/
      // reconnecting overlays. ontrack re-evaluates autoplay with media.
      setNeedsTapToPlay(false);

      // Tear down any previous connection before starting a fresh one.
      clearReconnectTimers();
      const prevController = controllerRef.current;
      const prevPc = pcRef.current;
      const prevResource = resourceUrlRef.current;
      controllerRef.current = null;
      pcRef.current = null;
      resourceUrlRef.current = null;
      prevController?.abort();
      if (prevPc) {
        prevPc.ontrack = null;
        prevPc.onconnectionstatechange = null;
        prevPc.close();
      }
      if (prevResource) {
        void fetch(prevResource, { method: "DELETE", keepalive: true }).catch(
          () => undefined,
        );
      }

      // Build a fresh peer connection with the low-latency constraints.
      const controller = new AbortController();
      const pc = new RTCPeerConnection();
      controllerRef.current = controller;
      pcRef.current = pc;

      pc.ontrack = (event) => {
        if (disposedRef.current || pcRef.current !== pc) return;
        setReceiverBufferTarget(event.receiver);
        const v = videoRef.current;
        if (!v) return;
        const [stream] = event.streams;
        v.srcObject = stream ?? new MediaStream([event.track]);
        attemptPlay();
      };

      pc.onconnectionstatechange = () => {
        if (disposedRef.current || pcRef.current !== pc) return;
        const state = pc.connectionState;
        if (state === "connected") {
          failuresRef.current = 0;
          failureKindRef.current = null;
          // Cancel BOTH the grace timer and any pending backoff-reconnect so a
          // pc that self-recovers is never torn down by a stale reconnect.
          clearReconnectTimers();
          setStatus("playing");
        } else if (state === "failed") {
          scheduleReconnect(0);
        } else if (state === "disconnected") {
          setNeedsTapToPlay(false);
          setStatus("reconnecting");
          if (graceTimerRef.current !== null) {
            clearTimeout(graceTimerRef.current);
          }
          graceTimerRef.current = setTimeout(() => {
            graceTimerRef.current = null;
            if (disposedRef.current || pcRef.current !== pc) return;
            if (pc.connectionState !== "connected") scheduleReconnect(0);
          }, DISCONNECT_GRACE_MS);
        }
      };

      pc.addTransceiver("video", { direction: "recvonly" });
      pc.addTransceiver("audio", { direction: "recvonly" });

      try {
        setError(null);

        await pc.setLocalDescription(await pc.createOffer());
        await waitForIceGatheringComplete(
          pc,
          controller.signal,
          ICE_GATHERING_TIMEOUT_MS,
        );

        const sdp = pc.localDescription?.sdp;
        if (!sdp) throw new Error("Failed to create WebRTC offer.");

        const token = await getLiveReadCredential(whepUrl, controller.signal);
        if (controller.signal.aborted || pcRef.current !== pc) return;
        const res = await fetch(whepUrl, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/sdp",
            "Content-Type": "application/sdp",
          },
          body: sdp,
          signal: controller.signal,
        });

        if (!res.ok) {
          // 404 (publisher not up yet), 408/429 and 5xx are transient and
          // worth the backoff schedule. Other client errors (e.g. 400
          // "codecs not supported by client") are deterministic — retrying
          // can never succeed, so fail fast instead, carrying the server's
          // reason into the terminal error message (#874).
          const retryable =
            res.status === 404 ||
            res.status === 408 ||
            res.status === 429 ||
            res.status >= 500;
          const reason = extractWhepReason(await res.text().catch(() => ""));
          if (!retryable) {
            failuresRef.current = MAX_FAILURES;
            if (reason) fatalReasonRef.current = reason.slice(0, 120);
          }
          throw new Error(
            `WHEP negotiation failed with ${res.status}${reason ? `: ${reason.slice(0, 120)}` : "."}`,
          );
        }

        const loc = res.headers.get("Location");
        if (loc) resourceUrlRef.current = new URL(loc, whepUrl).toString();

        const answer = await res.text();
        if (
          disposedRef.current ||
          controller.signal.aborted ||
          pcRef.current !== pc
        ) {
          return;
        }

        await pc.setRemoteDescription({ type: "answer", sdp: answer });
      } catch (e) {
        if (
          controller.signal.aborted ||
          disposedRef.current ||
          pcRef.current !== pc
        ) {
          return;
        }
        if (e instanceof DOMException && e.name === "AbortError") return;
        if (e instanceof DOMException && e.name === "TimeoutError") {
          failureKindRef.current = "gathering-timeout";
        } else {
          failureKindRef.current = null;
        }
        scheduleReconnect();
      }
    }

    // `connect` is async; funnel every call through here so a stray synchronous
    // throw can never surface as an unhandled rejection.
    function startConnect() {
      void connect().catch(() => undefined);
    }

    // Immediate reconnect that resets backoff (visibility/online/retry).
    function resetAndReconnect() {
      if (disposedRef.current) return;
      failuresRef.current = 0;
      clearReconnectTimers();
      setStatus("reconnecting");
      startConnect();
    }
    reconnectRef.current = resetAndReconnect;

    function handleVisibilityChange() {
      if (
        document.visibilityState === "visible" &&
        statusRef.current !== "playing"
      ) {
        resetAndReconnect();
      }
    }

    function handleOnline() {
      // Only force a reconnect if we're not already playing cleanly — a stray
      // `online` event (network flap the WebRTC session survived, an AP switch)
      // shouldn't tear down a healthy stream. A genuine drop surfaces via
      // `onconnectionstatechange` instead. Mirrors `handleVisibilityChange`.
      if (statusRef.current !== "playing") resetAndReconnect();
    }

    setStatus("connecting");
    setError(null);
    startConnect();

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("online", handleOnline);

    return () => {
      disposedRef.current = true;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("online", handleOnline);
      clearReconnectTimers();

      const controller = controllerRef.current;
      const pc = pcRef.current;
      const resource = resourceUrlRef.current;
      controllerRef.current = null;
      pcRef.current = null;
      resourceUrlRef.current = null;

      controller?.abort();
      if (pc) {
        pc.ontrack = null;
        pc.onconnectionstatechange = null;
        pc.close();
      }
      if (resource) {
        void fetch(resource, { method: "DELETE", keepalive: true }).catch(
          () => undefined,
        );
      }
      if (videoRef.current) videoRef.current.srcObject = null;
      reconnectRef.current = () => {};
    };
  }, [src, attemptPlay]);

  const retry = useCallback(() => {
    reconnectRef.current();
  }, []);

  return { videoRef, status, error, needsTapToPlay, play, retry };
}
